import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  confirmBillingReconciliationSuggestedMatch,
  listBillingReconciliationRecurringRules,
  listBillingReconciliationSuggestedMatches,
  promoteBillingReconciliationActionToRecurringRule,
  rejectBillingReconciliationSuggestedMatch,
  updateBillingReconciliationRecurringRule,
} from './billingReconciliationApi.js';

const recurringKeys = {
  suggestedMatches: ({ runId, bac, productFamily } = {}) => [
    'billingRecon',
    'suggestedMatches',
    runId || null,
    bac || null,
    productFamily || null,
  ],
  recurringRules: ({ program, status, limit, offset } = {}) => [
    'billingRecon',
    'recurringRules',
    program || null,
    status || null,
    limit || null,
    offset || null,
  ],
};

const noRetry = { retry: false };

const invalidateSuggestedMatchSurfaces = (queryClient, variables = {}) => {
  queryClient.invalidateQueries({ queryKey: ['billingRecon', 'suggestedMatches', variables?.runId] });
  queryClient.invalidateQueries({
    queryKey: variables?.bac
      ? ['billingRecon', 'operatorContext', variables?.runId, variables.bac]
      : ['billingRecon', 'operatorContext', variables?.runId],
    refetchType: 'active',
  });
};

export const useBillingReconciliationSuggestedMatches = (params = {}, options = {}) =>
  useQuery({
    queryKey: recurringKeys.suggestedMatches(params),
    queryFn: ({ signal }) => listBillingReconciliationSuggestedMatches({ ...params, signal }),
    enabled: (options.enabled ?? true) && Boolean(params.runId),
    staleTime: 15 * 1000,
    ...noRetry,
    ...options,
  });

export const useBillingReconciliationRecurringRules = (params = {}, options = {}) =>
  useQuery({
    queryKey: recurringKeys.recurringRules(params),
    queryFn: ({ signal }) => listBillingReconciliationRecurringRules({ ...params, signal }),
    enabled: options.enabled ?? true,
    staleTime: 30 * 1000,
    ...noRetry,
    ...options,
  });

export const useConfirmBillingReconciliationSuggestedMatch = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: confirmBillingReconciliationSuggestedMatch,
    onSuccess: (_data, variables) => {
      invalidateSuggestedMatchSurfaces(queryClient, variables);
      queryClient.invalidateQueries({ queryKey: ['billingRecon', 'runs'] });
      queryClient.invalidateQueries({ queryKey: ['billingRecon', 'varianceActions'] });
      queryClient.invalidateQueries({ queryKey: ['billingRecon', 'exportPreview'] });
    },
  });
};

export const useRejectBillingReconciliationSuggestedMatch = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: rejectBillingReconciliationSuggestedMatch,
    onSuccess: (_data, variables) => {
      invalidateSuggestedMatchSurfaces(queryClient, variables);
      queryClient.invalidateQueries({ queryKey: ['billingRecon', 'runs'] });
    },
  });
};

export const usePromoteBillingReconciliationActionToRecurringRule = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: promoteBillingReconciliationActionToRecurringRule,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['billingRecon', 'recurringRules'] });
      queryClient.invalidateQueries({ queryKey: ['billingRecon', 'runs'] });
    },
  });
};

export const useUpdateBillingReconciliationRecurringRule = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: updateBillingReconciliationRecurringRule,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['billingRecon', 'recurringRules'] });
      queryClient.invalidateQueries({ queryKey: ['billingRecon', 'suggestedMatches'] });
      queryClient.invalidateQueries({ queryKey: ['billingRecon', 'runs'] });
    },
  });
};
